import { CanAccess } from '@modules/common/components/CanAccess';
import { useNotificationContext } from '@modules/common/components/Notification/NotificationContext';
import { TypographyWithEllipsis } from '@modules/common/components/TypographyWithEllipsis';
import { AddReasonDefinitionsButton } from '@modules/taxonomyV2/components/AddReasonDefinitionsButton';
import { ReviewReasonName } from '@modules/taxonomyV2/components/CreateReasonForm/ReviewReasonName';
import { SelectedReasonDefinitionList } from '@modules/taxonomyV2/components/CreateReasonForm/SelectedReasonDefinitionList';
import { SuggestedReasonDefinitionsButton } from '@modules/taxonomyV2/components/CreateReasonForm/SuggestedReasonDefinitionsButton';
import { useCreateReason } from '@modules/taxonomyV2/hooks';
import { ReasonDefinition } from '@modules/taxonomyV2/store/types';
import { AutoAwesome, Edit } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import { Button, IconButton, Stack, Typography } from '@mui/material';
import {
  Category,
  GeneratedRcInput,
  MapIdToStringInput,
  PermissionOperation,
  PermissionResource,
  UnassignedRc,
  UnassignedRcInput,
} from 'generated/graphql';
import { uniqBy } from 'lodash';
import { useRef } from 'react';
import ErrorCode from 'v2/core/errors/codes';
import { isErrorCode } from 'v2/core/errors/errors';
import { toTitleCase } from 'v2/utils/strings';

interface CreateReasonStep2Props {
  prompt: string;
  reasonName: string;
  reasonCategory: Category;
  reasonDefinitions: ReasonDefinition[];
  onReasonNameChange: (reasonName: string) => void;
  onReasonDefinitionsChange: (reasonDefinitions: ReasonDefinition[]) => void;
  onReasonCategoryChange: (reasonCategory: Category) => void;
  onPromptEdit: () => void;
  onCompleted: (createdReason: {
    id: string;
    displayName: string;
    category: Category;
  }) => Promise<void> | void;
  onCancel: () => void;
  isLoading?: boolean;
}

export function CreateReasonStep2({
  prompt,
  reasonName,
  reasonCategory,
  reasonDefinitions,
  onReasonNameChange,
  onReasonCategoryChange,
  onReasonDefinitionsChange,
  onPromptEdit,
  onCompleted,
  onCancel,
  isLoading = false,
}: CreateReasonStep2Props) {
  const fixedSectionRef = useRef<HTMLDivElement | null>(null);
  const { showErrorNotification } = useNotificationContext();
  const [createReason, { loading: isCreateInProgress }] = useCreateReason({
    onError: (error) => {
      if (isErrorCode(error, ErrorCode.DuplicateRecord)) {
        return;
      }
      showErrorNotification({
        contentComponent: `Failed to create Reason. Error: ${error.message}`,
        props: {},
      });
    },
    onCompleted: (reasonId) => {
      onCompleted({
        id: reasonId,
        displayName: reasonName,
        category: reasonCategory,
      });
    },
  });

  const onSubmit = async () => {
    if (!reasonName) {
      return;
    }

    const selectedGeneratedRCs = reasonDefinitions
      ?.filter((definition) => definition.__typename === 'GeneratedRC')
      .map((definition): GeneratedRcInput => {
        return {
          name: definition.name,
          category: definition.category,
        };
      });

    const selectedUnassignedDefinitions = reasonDefinitions
      ?.filter(
        (definition): definition is UnassignedRc =>
          definition.__typename === 'UnassignedRC' && !!definition.isSelected,
      )
      .map((definition): UnassignedRcInput => {
        return {
          id: definition.id,
          name: definition.name,
          category: definition.category,
          recordIDs: definition.recordIDs,
        };
      });

    const feedbackRecords = selectedUnassignedDefinitions.map(
      (definition): MapIdToStringInput => {
        return {
          rc: definition.name,
          recordID: definition.recordIDs[0] ?? '',
        };
      },
    );

    await createReason({
      description: prompt,
      displayName: toTitleCase(reasonName),
      metadata: {
        category: reasonCategory,
        rc: {
          generatedRCs: selectedGeneratedRCs ?? [],
          unassignedRCs: selectedUnassignedDefinitions ?? [],
        },
      },
      records: feedbackRecords,
    });
  };

  const head = (
    <Stack direction="row" alignItems="center" justifyContent="space-between">
      <Typography variant="body1">Create Reason</Typography>
      <Stack direction="row" spacing={1}>
        <Button size="small" variant="outlined" onClick={onCancel}>
          Cancel
        </Button>
        <CanAccess
          resource={PermissionResource.Taxonomy}
          operation={PermissionOperation.Write}
        >
          <LoadingButton
            color="primary"
            variant="contained"
            type="submit"
            size="small"
            disabled={isCreateInProgress || isLoading}
            onClick={onSubmit}
          >
            Create
          </LoadingButton>
        </CanAccess>
      </Stack>
    </Stack>
  );

  const promptSection = (
    <Stack direction="row" alignItems="center">
      <TypographyWithEllipsis
        variant="caption"
        color="primary.main"
        flex={1}
        tooltipProps={{ placement: 'left', arrow: true }}
      >
        Prompt: {prompt}
      </TypographyWithEllipsis>
      <IconButton size="small" color="primary" onClick={() => onPromptEdit()}>
        <Edit sx={{ fontSize: '15px' }} />
      </IconButton>
    </Stack>
  );

  const definitionListHeader = (
    <>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="body2" color="text.secondary">
          Reason Definitions
        </Typography>
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <AutoAwesome fontSize="small" color="disabled" />
          <Typography variant="caption" color="text.secondary">
            Auto added
          </Typography>
        </Stack>
      </Stack>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <SuggestedReasonDefinitionsButton
          reasonDefinitions={reasonDefinitions}
          onChange={onReasonDefinitionsChange}
        />
        <AddReasonDefinitionsButton
          onChange={(rcs) => {
            onReasonDefinitionsChange(
              uniqBy([...reasonDefinitions, ...rcs], 'name'),
            );
          }}
        />
      </Stack>
    </>
  );

  return (
    <Stack height="100%" spacing={1}>
      <Stack spacing={2} ref={fixedSectionRef}>
        {head}
        {promptSection}
        <ReviewReasonName
          reasonName={reasonName}
          onReasonNameChange={onReasonNameChange}
          reasonCategory={reasonCategory}
          onCategoryChange={onReasonCategoryChange}
        />
        {definitionListHeader}
      </Stack>
      <Stack
        spacing={2}
        sx={{
          height: `calc(100% - ${fixedSectionRef.current?.offsetHeight}px)`,
          overflowY: 'auto',
        }}
      >
        <SelectedReasonDefinitionList
          reasonDefinitions={reasonDefinitions}
          onChange={onReasonDefinitionsChange}
        />
      </Stack>
    </Stack>
  );
}
